import { notifyUrlChange } from './urlChangeNotify';
import { canonicalJyotishaTopicId } from './jyotishaTopicCanonical';

/** Query key for deep-linking directly to a Jyotisha lesson/topic. */
export const JYOTISHA_TOPIC_QUERY_KEY = 'jyotishaTopic';

/** Optional: which nakshatra (1-27) is selected on Nakshatra personality notes. */
export const JYOTISHA_NAK_PROFILE_QUERY_KEY = 'nakProfile';

/** Home “Assess yourself” shortcut: single-question daily quiz (`quizAssess=1`). */
export const JYOTISHA_QUIZ_ASSESS_QUERY_KEY = 'quizAssess';

/**
 * @returns {string | null} topic id from URL query (raw value).
 */
export function readJyotishaTopicFromLocation() {
  if (typeof window === 'undefined') return null;
  const id = new URLSearchParams(window.location.search).get(JYOTISHA_TOPIC_QUERY_KEY);
  return id ? canonicalJyotishaTopicId(id) : null;
}

/**
 * Full shareable URL for current origin/path with Jyotisha topic query set.
 * @param {string} topicId
 */
export function buildJyotishaTopicShareUrl(topicId) {
  if (typeof window === 'undefined') return '';
  const u = new URL(window.location.href);
  u.searchParams.set(JYOTISHA_TOPIC_QUERY_KEY, topicId);
  return u.toString();
}

/**
 * Update browser URL without navigation (shareable link stays in sync).
 * @param {string | null} topicId, null removes the query param
 */
export function replaceJyotishaTopicInUrl(topicId) {
  if (typeof window === 'undefined') return;
  const u = new URL(window.location.href);
  if (topicId) u.searchParams.set(JYOTISHA_TOPIC_QUERY_KEY, topicId);
  else u.searchParams.delete(JYOTISHA_TOPIC_QUERY_KEY);
  window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
  notifyUrlChange();
}

/**
 * @returns {number | null} validated 1-27 from URL, or null.
 */
export function readNakProfileFromLocation() {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get(JYOTISHA_NAK_PROFILE_QUERY_KEY);
  if (raw == null || raw === '') return null;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1 || n > 27) return null;
  return n;
}

/**
 * @param {number | null | undefined} nakProfileId 1-27, or null/undefined to remove param
 */
export function replaceNakProfileInUrl(nakProfileId) {
  if (typeof window === 'undefined') return;
  const u = new URL(window.location.href);
  if (nakProfileId != null && nakProfileId >= 1 && nakProfileId <= 27) {
    u.searchParams.set(JYOTISHA_NAK_PROFILE_QUERY_KEY, String(nakProfileId));
  } else {
    u.searchParams.delete(JYOTISHA_NAK_PROFILE_QUERY_KEY);
  }
  window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
  notifyUrlChange();
}

/**
 * @returns {boolean} true when home “Assess yourself” single-question mode is active.
 */
export function readQuizAssessFromLocation() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get(JYOTISHA_QUIZ_ASSESS_QUERY_KEY) === '1';
}

/** @param {boolean} enabled */
export function setQuizAssessInUrl(enabled) {
  if (typeof window === 'undefined') return;
  const u = new URL(window.location.href);
  if (enabled) u.searchParams.set(JYOTISHA_QUIZ_ASSESS_QUERY_KEY, '1');
  else u.searchParams.delete(JYOTISHA_QUIZ_ASSESS_QUERY_KEY);
  window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
  notifyUrlChange();
}

export function clearQuizAssessFromUrl() {
  setQuizAssessInUrl(false);
}

/** Remove Jyotisha topic query when leaving Jyotisha. */
export function clearJyotishaTopicFromUrl() {
  if (typeof window === 'undefined') return;
  const u = new URL(window.location.href);
  let changed = false;
  if (u.searchParams.has(JYOTISHA_TOPIC_QUERY_KEY)) {
    u.searchParams.delete(JYOTISHA_TOPIC_QUERY_KEY);
    changed = true;
  }
  if (u.searchParams.has(JYOTISHA_NAK_PROFILE_QUERY_KEY)) {
    u.searchParams.delete(JYOTISHA_NAK_PROFILE_QUERY_KEY);
    changed = true;
  }
  if (u.searchParams.has(JYOTISHA_QUIZ_ASSESS_QUERY_KEY)) {
    u.searchParams.delete(JYOTISHA_QUIZ_ASSESS_QUERY_KEY);
    changed = true;
  }
  if (changed) {
    window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
    notifyUrlChange();
  }
}
